import { Box, Container, FormField, Header, SpaceBetween, Textarea } from '@amzn/awsui-components-react';
import { ArenaDefinition, ArenaScenario as ArenaScenarioType } from '@amzn/aws-jam-typescript-client';
import { isEqual } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LeavePageGuard from '@/src/components/common/LeavePageGuard/LeavePageGuard';
import { Nullable } from '@/src/types/common';
import { SaveArenaDefinition } from '@/src/utils/arena.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import ArenaEditActions from './ArenaEditActions';
import styles from './Arena.module.scss';

interface ArenaScenarioProps {
  arenaDefinition: Nullable<ArenaDefinition>;
  canEdit: boolean;
  onSave: SaveArenaDefinition;
}

const ArenaScenario: React.FC<ArenaScenarioProps> = ({ arenaDefinition, canEdit, onSave }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<ArenaScenarioType>({});

  const savedScenario: ArenaScenarioType = {
    scenarioBrief: arenaDefinition?.scenarioBrief ?? '',
    objective: arenaDefinition?.objective ?? '',
  };

  const hasUnsavedChanges = editing && !isEqual(draft, savedScenario);

  const handleEdit = () => {
    setDraft(savedScenario);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const saved = await onSave({ scenario: draft });
    setSaving(false);

    if (saved) {
      setEditing(false);
    }
  };

  const renderValue = (value: string | undefined) => {
    if (!value) {
      return <Box color="text-status-inactive">{t(i18nKeys.challenges.challengeDetails.arena.notSet)}</Box>;
    }

    return <Box variant="p">{value}</Box>;
  };

  const renderContent = () => {
    if (!editing) {
      return (
        <SpaceBetween size="l">
          <div>
            <Box variant="awsui-key-label">{t(i18nKeys.challenges.challengeDetails.arena.scenario.scenarioBrief)}</Box>
            {renderValue(arenaDefinition?.scenarioBrief)}
          </div>
          <div>
            <Box variant="awsui-key-label">{t(i18nKeys.challenges.challengeDetails.arena.scenario.objective)}</Box>
            {renderValue(arenaDefinition?.objective)}
          </div>
        </SpaceBetween>
      );
    }

    return (
      <SpaceBetween size="l">
        <FormField
          label={t(i18nKeys.challenges.challengeDetails.arena.scenario.scenarioBrief)}
          description={t(i18nKeys.challenges.challengeDetails.arena.scenario.scenarioBriefDescription)}
          stretch
        >
          <Textarea
            className={styles.autoGrow}
            value={draft.scenarioBrief ?? ''}
            rows={10}
            onChange={({ detail }) => setDraft({ ...draft, scenarioBrief: detail.value })}
            data-testid="arena-scenario-brief"
          />
        </FormField>
        <FormField
          label={t(i18nKeys.challenges.challengeDetails.arena.scenario.objective)}
          description={t(i18nKeys.challenges.challengeDetails.arena.scenario.objectiveDescription)}
          stretch
        >
          <Textarea
            className={styles.autoGrow}
            value={draft.objective ?? ''}
            rows={4}
            onChange={({ detail }) => setDraft({ ...draft, objective: detail.value })}
            data-testid="arena-scenario-objective"
          />
        </FormField>
      </SpaceBetween>
    );
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={t(i18nKeys.challenges.challengeDetails.arena.scenario.description)}
          actions={
            <ArenaEditActions
              canEdit={canEdit}
              editing={editing}
              saving={saving}
              onEdit={handleEdit}
              onCancel={() => setEditing(false)}
              onSave={() => void handleSave()}
            />
          }
        >
          {t(i18nKeys.challenges.challengeDetails.arena.scenario.title)}
        </Header>
      }
    >
      <LeavePageGuard when={hasUnsavedChanges} />
      {renderContent()}
    </Container>
  );
};

export default ArenaScenario;
